import cx from 'classnames';
import { LocationSelector } from 'components/admin/dashboard/training-campaigns/scheduler/setting-tabs/ManageLearnersSettingTab/PopulationRules/PopulationRulesBuilder/PopulationRuleRow';
import {
  getCustomCategoryValueLabel,
  getDepartmentLabel,
  getEmployeeTypeOptions,
  USER_PROFILE_ROLE_OPTIONS,
} from 'components/admin/datasets';
import { usePopulationAttributes } from 'components/admin/hooks';
import {
  Button,
  Form,
  ISelectOption,
  TableContainer,
} from 'components/admin/ui';
import { useAuthContext } from 'components/auth';
import { Modal, Tooltip } from 'components/ui';
import { prettyPrintCustomCategoryLabel } from 'lib/helpers/prettyPrint';
import { UserProfileRole } from 'lib/types/user';
import _ from 'lodash';
import { ReactNode } from 'react';
import {
  ISelectPopulationModalContext,
  SelectPopulationModalContext,
  useSelectPopulationModalContext,
} from './context';
import { useSearchCustomCategoryValues, useSearchDepartments } from './hooks';
import css from './SelectPopulationModal.module.scss';

const Filters = () => {
  const { session } = useAuthContext();
  const {
    isPreview,
    populationName,
    searchTerm,
    selectedEmployeeType,
    selectedLocations,
    selectedRole,
    setSearchTerm,
    setSelectedCustomCategoryValue,
    setSelectedDepartment,
    setSelectedEmployeeType,
    setSelectedLocations,
    setSelectedRole,
  } = useSelectPopulationModalContext();

  const { data: populationAttributes } = usePopulationAttributes({
    includeEmptyValues: true,
  });

  const employeeTypeOptions = getEmployeeTypeOptions(
    populationAttributes?.employeeTypes
  );

  return (
    <TableContainer.Header
      className={cx(css.tableHeader, { [css.preview]: isPreview })}
      isSticky
    >
      <Form.Control
        onChange={({ target }) => setSearchTerm(target.value)}
        placeholder={`Search ${_.toLower(populationName)}`}
        value={searchTerm}
        wrapperClassName="w-100"
      />
      <div className={css.filters}>
        <Form.Group className={css.group}>
          <Form.Label className={css.label}>Location</Form.Label>
          <LocationSelector
            locations={populationAttributes?.locations}
            onChange={setSelectedLocations}
            value={selectedLocations}
          />
        </Form.Group>

        <Form.Group className={css.group}>
          <Form.Label className={css.label}>Department</Form.Label>
          <Form.AsyncSelectV2<string>
            className={css.select}
            getLabel={getDepartmentLabel}
            onChange={setSelectedDepartment}
            useSearch={useSearchDepartments}
          />
        </Form.Group>

        <Form.Group className={css.group}>
          <Form.Label className={css.label}>
            {prettyPrintCustomCategoryLabel(
              session.company.customCategoryLabel
            )}
          </Form.Label>
          <Form.AsyncSelectV2<string>
            className={css.select}
            getLabel={(label: string) =>
              getCustomCategoryValueLabel(
                session.company.customCategoryLabel,
                label
              )
            }
            onChange={setSelectedCustomCategoryValue}
            useSearch={useSearchCustomCategoryValues}
          />
        </Form.Group>

        <Form.Group className={css.group}>
          <Form.Label className={css.label}>Role</Form.Label>
          <Form.Select
            className={css.select}
            isClearable
            onChange={(selected: ISelectOption<UserProfileRole>) =>
              setSelectedRole(selected?.value)
            }
            options={USER_PROFILE_ROLE_OPTIONS}
            value={_.find(
              USER_PROFILE_ROLE_OPTIONS,
              (option) => option.value === selectedRole
            )}
          />
        </Form.Group>

        <Form.Group className={css.group}>
          <Form.Label className={css.label}>Worker Type</Form.Label>
          <Form.Select
            className={css.select}
            isClearable
            onChange={(selected: ISelectOption<string>) =>
              setSelectedEmployeeType(selected?.value)
            }
            options={employeeTypeOptions}
            value={_.find(
              employeeTypeOptions,
              (option) => option.value === selectedEmployeeType
            )}
          />
        </Form.Group>
      </div>
    </TableContainer.Header>
  );
};

interface IBodyProps {
  children: ReactNode;
}

const Body = ({ children }: IBodyProps) => {
  const { isPreview } = useSelectPopulationModalContext();
  return (
    <Modal.Body className={css.modalBody}>
      <Filters />
      <TableContainer
        className={cx(css.tableContainer, { [css.preview]: isPreview })}
      >
        {children}
      </TableContainer>
    </Modal.Body>
  );
};

interface IFooterProps {
  selectAllTooltipLabel?: string;
  submitButtonLabel?: string;
}

const Footer = ({
  selectAllTooltipLabel = 'All active team members on the platform',
  submitButtonLabel,
}: IFooterProps) => {
  const {
    handleClose,
    handleDeselectAll,
    handleSelectAll,
    handleSubmit,
    handleTogglePreview,
    isPreview,
    isSelectAllVisible,
    populationName,
    selectedCount,
  } = useSelectPopulationModalContext();

  const renderSelectAllButton = () => {
    if (isPreview) {
      return null;
    }
    if (isSelectAllVisible) {
      if (selectedCount > 0) {
        return (
          <Button onClick={handleDeselectAll} variant="transparent">
            Deselect All
          </Button>
        );
      }
      return (
        <Tooltip
          className={css.tooltip}
          title={selectAllTooltipLabel}
          placement="top"
        >
          <Button onClick={handleSelectAll} variant="transparent">
            Select All
          </Button>
        </Tooltip>
      );
    }
  };

  return (
    <Modal.Footer className={css.modalFooter}>
      <div className="d-flex align-items-center">
        <p className={css.selectedCount}>Selected: {selectedCount}</p>
        <Button
          disabled={!isPreview && selectedCount === 0}
          onClick={handleTogglePreview}
          variant="transparent"
        >
          {isPreview ? 'Close Preview' : 'Preview'}
        </Button>
      </div>
      <div>
        {renderSelectAllButton()}
        <Button onClick={handleClose} variant="outline">
          Cancel
        </Button>
        <Button disabled={selectedCount === 0} onClick={handleSubmit}>
          {submitButtonLabel || `Add ${populationName}`}
        </Button>
      </div>
    </Modal.Footer>
  );
};

interface ISelectPopulationModalProps
  extends Omit<ISelectPopulationModalContext, 'handleSubmit'> {
  children: ReactNode;
  handleSubmit: () => void;
  isVisible: boolean;
  title?: ReactNode;
}

const SelectPopulationModal = ({
  children,
  handleSubmit,
  isVisible,
  title,
  ...contextProps
}: ISelectPopulationModalProps) => {
  const { handleClose, isPreview, populationName, selectedUserIds } =
    contextProps;
  return (
    <SelectPopulationModalContext.Provider
      value={{ ...contextProps, handleSubmit }}
    >
      <Modal
        className={css.modal}
        hasCloseButton
        keyboard={false}
        onHide={handleClose}
        show={isVisible}
      >
        <Modal.Header className={css.modalHeader}>
          {isPreview
            ? `Selected ${_.toLower(populationName)}: ${_.size(
                selectedUserIds
              )}`
            : title || `Find and add ${_.toLower(populationName)}`}
        </Modal.Header>
        {children}
      </Modal>
    </SelectPopulationModalContext.Provider>
  );
};

SelectPopulationModal.Body = Body;
SelectPopulationModal.Footer = Footer;

export { SelectPopulationModal };
